import { Link } from "react-router";
import { Layout } from "@/components/layout/Layout";
import { TargetDemo } from "@/components/marketing/TargetDemo";
import { useAuth } from "@/data/AuthProvider";

/**
 * Home.
 *
 * Rebuilt from git HEAD rather than `trade-r-archive/` — production was about
 * four and a half months behind master, so the archived page is an older
 * design ("CRYPTO CRUSHER", numbered step sections) that was never the intent.
 *
 * Rebuilt, not ported. The HEAD page is seven Bulma `hero` bands of alternating
 * colour, three of them phone screenshots of the 2020 UI that no longer exists,
 * and its own headline ("The Ins & Outs of Trading") says less than the demo
 * directly beneath it does. The content thesis is kept and the presentation is
 * not.
 *
 * The screenshots are deliberately not replaced with new ones. The interactive
 * demo *is* the product, running live — a picture of it would be strictly
 * worse, and would go stale the next time the UI moves.
 *
 * Copy note: this page leads with the reader's problem, not the mechanism.
 * "Decide when to get out, before you get in" described how the product works,
 * which the demo underneath demonstrates anyway. The headline's job is to make
 * someone who has held a winner too long recognise themselves in one line.
 * Short sentences, contractions, fragments where they land harder — the
 * register here is deliberately looser than the app screens, which stay
 * factual because they report your money back to you.
 */
const STEPS = [
  {
    n: "01",
    title: "Pick two numbers",
    body: "Where you take profit. Where you cut the loss. Decide it before you have money on the line.",
  },
  {
    n: "02",
    title: "Buy wherever you buy",
    body: "Coinbase, Kraken, whatever you use. Trade-r never touches your funds and never asks for keys.",
  },
  {
    n: "03",
    title: "Log it",
    body: "Entry price, your two numbers, done. That's the whole form.",
  },
  {
    n: "04",
    title: "Do what you said",
    body: "Trade-r tells you the moment a line breaks. The rest is on you.",
  },
];

const NOT_A = [
  "Not an exchange. Trade-r never holds or moves your money.",
  "Not connected to anything. No API keys, no wallet access, nothing to authorise.",
  "Not advice. It tells you when your own line breaks. Nothing else.",
];

export function Home() {
  const authState = useAuth();
  const signedIn = authState.status === "signedIn";

  return (
    <Layout title="Plan your crypto exits">
      {/* ── The claim ────────────────────────────────────────── */}
      <section className="rule">
        <div className="mx-auto max-w-4xl px-6 py-20">
          <p className="eyebrow">Crypto exit planner</p>
          {/*
            Block spans separated by explicit `{" "}` text nodes, not <br>.
            Accessible-name computation concatenates descendant text, and a
            line break contributes nothing — a bare <br> here would leave the
            name reading "You knew when to sell.You just didn't." The spaces
            are real text nodes so they survive whether or not CSS has loaded.
          */}
          {/* Broken at the phrase, and short line first: 8/13/16 characters
              steps down the rag instead of jagging. */}
          <h1 className="statement mt-6 -ml-1">
            <span className="block">You knew</span>{" "}
            <span className="block">when to sell.</span>{" "}
            <span className="block text-ink-soft">You just didn't.</span>
          </h1>
          <p className="mt-8 max-w-xl text-lg">
            Set both exits before you buy. Trade-r watches the price and tells you the second one
            hits. No hoping. No "just a bit longer".
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            {signedIn ? (
              <Link to="/trade/list" className="btn-accent">
                Go to your trades
              </Link>
            ) : (
              <>
                <Link to="/user/start" className="btn-accent">
                  Start free
                </Link>
                <Link to="/user/sign-in" className="btn-outline">
                  Sign in
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ── The product, running ─────────────────────────────── */}
      <TargetDemo />

      {/* ── How it works ─────────────────────────────────────── */}
      <section className="border-t-2 border-ink">
        <div className="mx-auto max-w-5xl px-6 py-16">
          <h2 className="text-3xl font-black tracking-tighter sm:text-4xl">How it works</h2>

          <ol className="facts mt-10 grid-cols-1 border-2 border-ink sm:grid-cols-2">
            {STEPS.map((step) => (
              <li key={step.n}>
                <p className="eyebrow">{step.n}</p>
                <h3 className="mt-2 text-xl font-black tracking-tight">{step.title}</h3>
                <p className="mt-2 text-sm">{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── What it measures ─────────────────────────────────── */}
      <section className="field-accent border-t-2 border-ink">
        <div className="mx-auto max-w-4xl px-6 py-16">
          <h2 className="text-3xl font-black tracking-tighter sm:text-4xl">
            Made money but bailed early? Still not a win.
          </h2>
          <p className="mt-4 max-w-xl">
            Trade-r only scores a trade as won if it reached the target you set. Harsh. It's also
            the only number that tells you whether you're getting better or just getting lucky.
          </p>
          <Link to="/faqs" className="btn-primary mt-8">
            Read the FAQs
          </Link>
        </div>
      </section>

      {/* ── Honest limits ────────────────────────────────────── */}
      <section className="border-t-2 border-ink">
        <div className="mx-auto max-w-5xl px-6 py-16">
          <h2 className="text-2xl font-black tracking-tighter">What it isn't</h2>
          <ul className="mt-6 max-w-2xl space-y-3">
            {NOT_A.map((line) => (
              <li key={line} className="border-l-2 border-ink pl-4 text-sm">
                {line}
              </li>
            ))}
          </ul>

          {!signedIn && (
            <Link to="/user/start" className="btn-primary mt-10">
              Start free
            </Link>
          )}
        </div>
      </section>
    </Layout>
  );
}
